/**
 * SNAKE-30: smart hint shown under the PLAY button on the main menu.
 *
 * The hint surfaces one of four states. Spec from the GDD table:
 *
 *   top-10           → "🔥 Ты #N! Удержи позицию →"
 *   ranked-but-low   → "#N · K⭐ сегодня →"
 *   not-played-yet   → "Стань первым сегодня →"
 *   leaderboard-empty → no line at all (renderer hides element)
 *
 * Lives in its own module — pure formatter, no DOM, easy to unit-test
 * without dragging in NetworkManager.
 */

export interface StatusLineInput {
  /** Player's rank on the daily leaderboard today, or null if unranked. */
  myDailyRank: { rank: number; score: number } | null
  /** Whether the player has finished at least one match today. */
  hasPlayedToday: boolean
  /** Whether the leaderboard has any entries at all (early-morning UTC). */
  leaderboardHasEntries: boolean
  /**
   * True once the menu has received a bootstrap response. Without this guard
   * a fresh client (or one with a failing /api/menu/bootstrap call) would sit
   * on `leaderboardHasEntries=false` indefinitely and silently hide the line —
   * but we still want to show "Стань первым сегодня" once we know the board
   * is genuinely empty.
   */
  bootstrapLoaded: boolean
}

export interface StatusLine {
  kind: 'top10' | 'ranked' | 'not_played' | 'hidden'
  text: string
}

const TOP_THRESHOLD = 10

export function buildStatusLine(input: StatusLineInput): StatusLine {
  // Pre-bootstrap: keep the line hidden so we don't render a CTA before we
  // know whether the player is in the top 10. The first successful bootstrap
  // flips `bootstrapLoaded` to true.
  if (!input.bootstrapLoaded) {
    return { kind: 'hidden', text: '' }
  }

  // Bootstrap loaded but board is empty (early-morning UTC) → spec hides
  // the line. We rely on the consumer to keep leaderboardHasEntries
  // truthful: it's true when topToday is non-empty OR the player has a rank.
  if (!input.leaderboardHasEntries) {
    return { kind: 'hidden', text: '' }
  }

  const rank = input.myDailyRank?.rank
  const score = input.myDailyRank?.score

  if (typeof rank === 'number' && rank >= 1 && rank <= TOP_THRESHOLD) {
    return { kind: 'top10', text: `🔥 Ты #${rank}! Удержи позицию →` }
  }

  if (typeof rank === 'number' && typeof score === 'number' && rank > TOP_THRESHOLD) {
    return { kind: 'ranked', text: `#${rank} · ${score}⭐ сегодня →` }
  }

  // Hasn't played today (or has no rank yet) but the leaderboard exists → CTA.
  if (!input.hasPlayedToday) {
    return { kind: 'not_played', text: 'Стань первым сегодня →' }
  }

  // Edge: hasPlayedToday=true but rank is null. Shouldn't happen in practice,
  // but guard against it — fall back to the "ranked but no rank yet" CTA so we
  // never render a blank, non-empty line.
  return { kind: 'not_played', text: 'Стань первым сегодня →' }
}
